import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useState, useMemo } from "react";
import {
  Plus, Search, Globe, ArrowUp, ArrowDown, Minus, AlertTriangle, CheckCircle2, Sparkles,
  TrendingUp, TrendingDown, BarChart3, Eye, Link2, FileText, ShieldCheck, Target,
  XCircle, ChevronLeft, ChevronRight, Trash2, ExternalLink, Award, Zap, Gauge,
  AlertCircle, Info, RefreshCw, Smartphone, Monitor, Lock, Database,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, ComposedChart, Legend,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from "recharts";

/* ──────── Constants ──────── */
const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "keywords", label: "Keywords", icon: Search },
  { id: "audit", label: "Site Audit", icon: ShieldCheck },
  { id: "backlinks", label: "Backlinks", icon: Link2 },
  { id: "competitors", label: "Competitors", icon: Target },
  { id: "pages", label: "Pages", icon: FileText },
  { id: "ai", label: "AI SEO", icon: Sparkles },
] as const;

const INTENT_COLORS: Record<string, string> = { informational: "#3b82f6", transactional: "#10b981", commercial: "#f59e0b", navigational: "#8b5cf6" };
const INTENT_LABELS: Record<string, string> = { informational: "Info", transactional: "Transact", commercial: "Commercial", navigational: "Nav" };
const RANK_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#f97316", "#ef4444", "#9ca3af"];
const SEV_COLORS: Record<string, string> = { critical: "text-red-600 bg-red-50 border-red-200", warning: "text-yellow-700 bg-yellow-50 border-yellow-200", info: "text-blue-600 bg-blue-50 border-blue-200" };
const SEV_ICONS: Record<string, any> = { critical: XCircle, warning: AlertTriangle, info: Info };

/* ──────── Helpers ──────── */
const fmt = (n: number | null | undefined) => n == null ? "—" : n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n);
const fmtMoney = (n: number | null | undefined) => n == null ? "—" : `$${n.toFixed(2)}`;
const posColor = (p: number | null) => !p ? "text-gray-400" : p <= 3 ? "text-green-600" : p <= 10 ? "text-blue-600" : p <= 20 ? "text-yellow-600" : p <= 50 ? "text-orange-500" : "text-red-500";
const scoreColor = (s: number | null) => !s && s !== 0 ? "text-gray-400" : s >= 80 ? "text-green-600" : s >= 60 ? "text-yellow-600" : s >= 40 ? "text-orange-500" : "text-red-500";
const scoreGrade = (s: number | null) => !s && s !== 0 ? "—" : s >= 90 ? "A+" : s >= 80 ? "A" : s >= 70 ? "B" : s >= 60 ? "C" : s >= 50 ? "D" : "F";
const Delta = ({ v }: { v: number }) => v > 0 ? <span className="flex items-center text-green-600 text-xs font-medium"><ArrowUp className="w-3 h-3" />{v}</span> : v < 0 ? <span className="flex items-center text-red-600 text-xs font-medium"><ArrowDown className="w-3 h-3" />{Math.abs(v)}</span> : <Minus className="w-3 h-3 text-gray-400" />;

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════ */
export default function SEOTools() {
  const qc = useQueryClient();
  const [selectedProject, setSelectedProject] = useState<string | null>(null);
  const [tab, setTab] = useState<string>("overview");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ domain: "", name: "", country: "US", language: "en", searchEngine: "google", crawlFrequency: "weekly" });

  /* ─── Queries ─── */
  const { data: projects } = useQuery({ queryKey: ["seo-projects"], queryFn: () => api.get<any[]>("/seo/projects") });
  const { data: overview } = useQuery({ queryKey: ["seo-overview"], queryFn: () => api.get<any>("/seo/overview") });
  const { data: rankDist } = useQuery({ queryKey: ["seo-rank-dist"], queryFn: () => api.get<any[]>("/seo/rank-distribution") });
  const { data: visTL } = useQuery({ queryKey: ["seo-vis-tl"], queryFn: () => api.get<any[]>("/seo/visibility-timeline?days=30") });
  const { data: project, refetch: refetchProject } = useQuery({
    queryKey: ["seo-project", selectedProject], queryFn: () => api.get<any>(`/seo/projects/${selectedProject}`), enabled: !!selectedProject,
  });
  const { data: blData } = useQuery({
    queryKey: ["seo-backlinks", selectedProject], queryFn: () => api.get<any>(`/seo/projects/${selectedProject}/backlinks`), enabled: !!selectedProject,
  });
  const { data: pagesData } = useQuery({
    queryKey: ["seo-pages", selectedProject], queryFn: () => api.get<any>(`/seo/projects/${selectedProject}/pages`), enabled: !!selectedProject,
  });
  const { data: competitorsData } = useQuery({
    queryKey: ["seo-competitors", selectedProject], queryFn: () => api.get<any[]>(`/seo/projects/${selectedProject}/competitors`), enabled: !!selectedProject,
  });

  /* ─── Mutations ─── */
  const createMut = useMutation({
    mutationFn: (b: any) => api.post("/seo/projects", b),
    onSuccess: (d: any) => { qc.invalidateQueries({ queryKey: ["seo-projects"] }); qc.invalidateQueries({ queryKey: ["seo-overview"] }); setSelectedProject(d.id); setShowCreate(false); setForm({ domain: "", name: "", country: "US", language: "en", searchEngine: "google", crawlFrequency: "weekly" }); },
  });

  const deleteKwMut = useMutation({
    mutationFn: (id: string) => api.delete(`/seo/keywords/${id}`),
    onSuccess: () => { refetchProject(); qc.invalidateQueries({ queryKey: ["seo-overview"] }); },
  });

  const ov = overview;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SEO Tools</h1>
          <p className="text-sm text-gray-500 mt-1">Track keywords, audit sites, analyze backlinks & outpace competitors</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus className="w-4 h-4" /> New Project</button>
        </div>
      </div>

      {/* Project selector pills */}
      {projects && projects.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setSelectedProject(null); setTab("overview"); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!selectedProject ? "bg-gradient-to-r from-violet-600 to-pink-500 text-white shadow" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            All Projects
          </button>
          {projects.map((p: any) => (
            <button key={p.id} onClick={() => setSelectedProject(p.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selectedProject === p.id ? "bg-gradient-to-r from-violet-600 to-pink-500 text-white shadow" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
              <Globe className="w-3 h-3 inline mr-1" />{p.name}
            </button>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? "border-violet-600 text-violet-700" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {tab === "overview" && <OverviewTab ov={ov} rankDist={rankDist} visTL={visTL} projects={projects} />}
      {tab === "keywords" && <KeywordsTab project={project} deleteKw={deleteKwMut.mutate} refetch={refetchProject} selectedProject={selectedProject} />}
      {tab === "audit" && <AuditTab project={project} />}
      {tab === "backlinks" && <BacklinksTab data={blData} />}
      {tab === "competitors" && <CompetitorsTab data={competitorsData} project={project} />}
      {tab === "pages" && <PagesTab data={pagesData} />}
      {tab === "ai" && <AITab projects={projects} />}

      {/* No project selected hint */}
      {!selectedProject && ["keywords", "audit", "backlinks", "competitors", "pages"].includes(tab) && (
        <div className="card p-12 text-center">
          <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-semibold text-gray-700 mb-1">Select a Project</h3>
          <p className="text-sm text-gray-400">Choose a project above to view {tab} data</p>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-violet-600 via-purple-500 to-pink-500 px-6 py-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Globe className="w-5 h-5" /> New SEO Project</h2>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(form); }} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className="label">Domain *</label><input className="input" placeholder="example.com" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} required /></div>
                <div className="col-span-2"><label className="label">Project Name</label><input className="input" placeholder="Main Website" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><label className="label">Country</label>
                  <select className="input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}>
                    <option value="US">🇺🇸 United States</option><option value="GB">🇬🇧 United Kingdom</option><option value="CA">🇨🇦 Canada</option><option value="AU">🇦🇺 Australia</option><option value="DE">🇩🇪 Germany</option><option value="FR">🇫🇷 France</option><option value="ES">🇪🇸 Spain</option><option value="MX">🇲🇽 Mexico</option><option value="BR">🇧🇷 Brazil</option><option value="HT">🇭🇹 Haiti</option>
                  </select>
                </div>
                <div><label className="label">Search Engine</label>
                  <select className="input" value={form.searchEngine} onChange={(e) => setForm({ ...form, searchEngine: e.target.value })}>
                    <option value="google">Google</option><option value="bing">Bing</option><option value="yahoo">Yahoo</option>
                  </select>
                </div>
                <div><label className="label">Language</label>
                  <select className="input" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
                    <option value="en">English</option><option value="es">Spanish</option><option value="fr">French</option><option value="de">German</option><option value="pt">Portuguese</option><option value="ht">Haitian Creole</option>
                  </select>
                </div>
                <div><label className="label">Crawl Frequency</label>
                  <select className="input" value={form.crawlFrequency} onChange={(e) => setForm({ ...form, crawlFrequency: e.target.value })}>
                    <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="biweekly">Bi-weekly</option><option value="monthly">Monthly</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
                <button type="submit" disabled={createMut.isPending} className="btn-primary">{createMut.isPending ? "Creating..." : "Create Project"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   OVERVIEW TAB
   ════════════════════════════════════════════════════════════ */
function OverviewTab({ ov, rankDist, visTL, projects }: any) {
  if (!ov) return <div className="text-center py-12 text-gray-400">Loading overview...</div>;

  const kpis = [
    { label: "Projects", value: ov.totalProjects, icon: Globe, color: "text-violet-600 bg-violet-50" },
    { label: "Keywords Tracked", value: ov.totalKeywords, icon: Search, color: "text-blue-600 bg-blue-50" },
    { label: "Avg Position", value: ov.avgPosition ? `#${ov.avgPosition}` : "—", icon: TrendingUp, color: "text-green-600 bg-green-50" },
    { label: "Visibility Score", value: `${ov.visibilityScore}%`, icon: Eye, color: "text-amber-600 bg-amber-50" },
    { label: "Backlinks", value: fmt(ov.totalBacklinks), icon: Link2, color: "text-pink-600 bg-pink-50" },
    { label: "Issues Found", value: ov.totalIssues, icon: AlertTriangle, color: ov.totalIssues > 10 ? "text-red-600 bg-red-50" : "text-yellow-600 bg-yellow-50" },
    { label: "Audit Score", value: ov.auditScoreAvg != null ? `${ov.auditScoreAvg}/100` : "—", icon: ShieldCheck, color: "text-emerald-600 bg-emerald-50" },
    { label: "Top 10 Rankings", value: (ov.rankDistribution?.top3 || 0) + (ov.rankDistribution?.top10 || 0), icon: Award, color: "text-indigo-600 bg-indigo-50" },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="card p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${k.color}`}><Icon className="w-5 h-5" /></div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{k.value}</p>
                  <p className="text-xs text-gray-500">{k.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Visibility Timeline */}
        <div className="lg:col-span-2 card p-5">
          <h3 className="text-sm font-semibold mb-4">Visibility & Position Trend (30 Days)</h3>
          {visTL?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={visTL}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="vis" tick={{ fontSize: 11 }} domain={[0, 100]} />
                <YAxis yAxisId="pos" orientation="right" reversed tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Area yAxisId="vis" type="monotone" dataKey="visibility" name="Visibility %" fill="#8b5cf6" stroke="#8b5cf6" fillOpacity={0.15} />
                <Line yAxisId="pos" type="monotone" dataKey="avgPosition" name="Avg Position" stroke="#ec4899" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <p className="text-gray-400 text-sm text-center py-8">No data yet</p>}
        </div>

        {/* Rank Distribution */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold mb-4">Rank Distribution</h3>
          {rankDist?.length ? (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={rankDist.filter((b: any) => b.count > 0)} dataKey="count" nameKey="range" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {rankDist.filter((b: any) => b.count > 0).map((_: any, i: number) => <Cell key={i} fill={RANK_COLORS[i % RANK_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {rankDist.map((b: any, i: number) => (
                  <div key={b.range} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: RANK_COLORS[i] }} />{b.range}</span>
                    <span className="font-semibold">{b.count}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-gray-400 text-sm text-center py-8">No data</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Movers */}
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50"><h3 className="text-sm font-semibold">Top Movers (Position Change)</h3></div>
          {ov.topMovers?.length ? (
            <div className="divide-y divide-gray-50">
              {ov.topMovers.map((m: any, i: number) => (
                <div key={i} className="px-5 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">{m.keyword}</p>
                    <p className="text-xs text-gray-400 truncate max-w-[250px]">{m.url}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-bold ${posColor(m.position)}`}>#{m.position}</span>
                    <Delta v={m.change} />
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="p-6 text-center text-gray-400 text-sm">No position changes yet</p>}
        </div>

        {/* Recent Audits */}
        <div className="card overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50"><h3 className="text-sm font-semibold">Recent Site Audits</h3></div>
          {ov.recentAudits?.length ? (
            <div className="divide-y divide-gray-50">
              {ov.recentAudits.map((a: any) => (
                <div key={a.id} className="px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${a.score >= 80 ? "bg-green-50 text-green-600" : a.score >= 60 ? "bg-yellow-50 text-yellow-600" : "bg-red-50 text-red-600"}`}>{a.score}</div>
                    <div>
                      <p className="text-sm font-medium">{a.projectName}</p>
                      <p className="text-xs text-gray-400">{a.pagesAudited} pages · {new Date(a.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 text-xs">
                    {a.criticalIssues > 0 && <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600">{a.criticalIssues} critical</span>}
                    {a.warnings > 0 && <span className="px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700">{a.warnings} warnings</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="p-6 text-center text-gray-400 text-sm">No audits yet</p>}
        </div>
      </div>

      {/* Project Cards */}
      {projects?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">All Projects</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {projects.map((p: any) => (
              <div key={p.id} className="card p-4 hover:shadow-lg transition-shadow cursor-pointer group">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-sm group-hover:text-violet-600 transition-colors">{p.name}</p>
                    <p className="text-xs text-gray-400 flex items-center gap-1"><Globe className="w-3 h-3" />{p.domain}</p>
                  </div>
                  {p.healthScore != null && (
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xs font-bold ${p.healthScore >= 80 ? "bg-green-50 text-green-600" : p.healthScore >= 60 ? "bg-yellow-50 text-yellow-600" : "bg-red-50 text-red-600"}`}>
                      {p.healthScore}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div><p className="text-lg font-bold text-gray-900">{p._count?.keywordRanks || 0}</p><p className="text-[10px] text-gray-400">Keywords</p></div>
                  <div><p className="text-lg font-bold text-gray-900">{p._count?.seoBacklinks || 0}</p><p className="text-[10px] text-gray-400">Backlinks</p></div>
                  <div><p className="text-lg font-bold text-gray-900">{p._count?.seoPages || 0}</p><p className="text-[10px] text-gray-400">Pages</p></div>
                  <div><p className="text-lg font-bold text-gray-900">{p._count?.seoAudits || 0}</p><p className="text-[10px] text-gray-400">Audits</p></div>
                </div>
                <div className="flex gap-2 mt-3 text-[10px] text-gray-400">
                  <span className="bg-gray-100 px-1.5 py-0.5 rounded">{p.searchEngine}</span>
                  <span className="bg-gray-100 px-1.5 py-0.5 rounded">{p.country}</span>
                  <span className="bg-gray-100 px-1.5 py-0.5 rounded">{p.crawlFrequency}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   KEYWORDS TAB
   ════════════════════════════════════════════════════════════ */
function KeywordsTab({ project, deleteKw, refetch, selectedProject }: any) {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<string>("position");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [filterIntent, setFilterIntent] = useState<string>("all");
  const [showAdd, setShowAdd] = useState(false);
  const [kwInput, setKwInput] = useState("");
  const qc = useQueryClient();

  const addKwMut = useMutation({
    mutationFn: (body: any) => api.post(`/seo/projects/${selectedProject}/keywords`, body),
    onSuccess: () => { refetch(); qc.invalidateQueries({ queryKey: ["seo-overview"] }); setShowAdd(false); setKwInput(""); },
  });

  if (!project) return null;
  const keywords = project.keywordRanks || [];

  const filtered = useMemo(() => {
    let kws = [...keywords];
    if (search) kws = kws.filter((k: any) => k.keyword.toLowerCase().includes(search.toLowerCase()));
    if (filterIntent !== "all") kws = kws.filter((k: any) => k.intent === filterIntent);
    kws.sort((a: any, b: any) => {
      let va = a[sortBy], vb = b[sortBy];
      if (va == null) va = sortDir === "asc" ? Infinity : -Infinity;
      if (vb == null) vb = sortDir === "asc" ? Infinity : -Infinity;
      return sortDir === "asc" ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
    return kws;
  }, [keywords, search, sortBy, sortDir, filterIntent]);

  const toggleSort = (col: string) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };
  const SH = ({ col, label }: { col: string; label: string }) => (
    <th onClick={() => toggleSort(col)} className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700 select-none">
      {label} {sortBy === col && (sortDir === "asc" ? "↑" : "↓")}
    </th>
  );

  // Stats
  const positioned = keywords.filter((k: any) => k.position);
  const top3 = positioned.filter((k: any) => k.position <= 3).length;
  const top10 = positioned.filter((k: any) => k.position <= 10).length;
  const avgPos = positioned.length ? (positioned.reduce((s: number, k: any) => s + k.position, 0) / positioned.length).toFixed(1) : "—";
  const featured = keywords.filter((k: any) => k.featured).length;

  return (
    <div className="space-y-4">
      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { l: "Total Keywords", v: keywords.length },
          { l: "Avg Position", v: `#${avgPos}` },
          { l: "Top 3", v: top3 },
          { l: "Top 10", v: top10 },
          { l: "Featured Snippets", v: featured },
        ].map((s) => (
          <div key={s.l} className="card p-3 text-center"><p className="text-xl font-bold text-gray-900">{s.v}</p><p className="text-[10px] text-gray-500">{s.l}</p></div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="input pl-9" placeholder="Search keywords..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select className="input w-auto" value={filterIntent} onChange={(e) => setFilterIntent(e.target.value)}>
          <option value="all">All Intents</option>
          <option value="informational">Informational</option>
          <option value="transactional">Transactional</option>
          <option value="commercial">Commercial</option>
          <option value="navigational">Navigational</option>
        </select>
        <button onClick={() => setShowAdd(true)} className="btn-primary text-sm"><Plus className="w-4 h-4" /> Add Keywords</button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {filtered.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <SH col="keyword" label="Keyword" />
                  <SH col="position" label="Position" />
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Change</th>
                  <SH col="searchVolume" label="Volume" />
                  <SH col="difficulty" label="Difficulty" />
                  <SH col="cpc" label="CPC" />
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Intent</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Features</th>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">URL</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((kw: any) => {
                  const change = kw.previousPos && kw.position ? kw.previousPos - kw.position : 0;
                  return (
                    <tr key={kw.id} className="hover:bg-gray-50/50">
                      <td className="px-4 py-3 text-sm font-medium max-w-[220px]">
                        <span>{kw.keyword}</span>
                        {kw.bestPosition && <span className="block text-[10px] text-gray-400">Best: #{kw.bestPosition}</span>}
                      </td>
                      <td className="px-4 py-3">
                        {kw.position ? <span className={`text-sm font-bold ${posColor(kw.position)}`}>#{kw.position}</span> : <span className="text-gray-400 text-sm">—</span>}
                      </td>
                      <td className="px-4 py-3"><Delta v={change} /></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{kw.searchVolume?.toLocaleString() || "—"}</td>
                      <td className="px-4 py-3">
                        {kw.difficulty != null ? (
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full rounded-full ${kw.difficulty <= 30 ? "bg-green-500" : kw.difficulty <= 60 ? "bg-yellow-500" : "bg-red-500"}`} style={{ width: `${kw.difficulty}%` }} /></div>
                            <span className="text-xs text-gray-600 w-6">{kw.difficulty}</span>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{kw.cpc ? fmtMoney(kw.cpc) : "—"}</td>
                      <td className="px-4 py-3">
                        {kw.intent && <span className="text-[10px] px-1.5 py-0.5 rounded font-medium" style={{ background: `${INTENT_COLORS[kw.intent]}15`, color: INTENT_COLORS[kw.intent] }}>{INTENT_LABELS[kw.intent] || kw.intent}</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {kw.featured && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600">Featured</span>}
                          {kw.localPack && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">Local</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-400 truncate max-w-[180px]" title={kw.url}>{kw.url ? kw.url.replace(/^https?:\/\//, "").slice(0, 35) : "—"}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => { if (confirm("Delete this keyword?")) deleteKw(kw.id); }} className="text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-gray-400">
            <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
            {keywords.length ? "No keywords match your filters" : "No keywords tracked yet"}
          </div>
        )}
      </div>

      {/* Add Keywords Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-3">Add Keywords</h2>
            <p className="text-xs text-gray-500 mb-3">Enter keywords separated by commas or new lines</p>
            <textarea className="input h-32 resize-none" placeholder="cloud hosting, managed wordpress, VPS hosting..." value={kwInput} onChange={(e) => setKwInput(e.target.value)} />
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowAdd(false)} className="btn-secondary">Cancel</button>
              <button onClick={() => {
                const kws = kwInput.split(/[,\n]/).map((k) => k.trim()).filter(Boolean);
                if (kws.length) addKwMut.mutate({ keywords: kws });
              }} disabled={!kwInput.trim() || addKwMut.isPending} className="btn-primary">{addKwMut.isPending ? "Adding..." : `Add ${kwInput.split(/[,\n]/).filter((k) => k.trim()).length} Keywords`}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   SITE AUDIT TAB
   ════════════════════════════════════════════════════════════ */
function AuditTab({ project }: any) {
  if (!project) return null;
  const audits = project.seoAudits || [];
  const latest = audits[0];

  if (!latest) return (
    <div className="card p-12 text-center">
      <ShieldCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 className="font-semibold text-gray-700 mb-1">No Audits Yet</h3>
      <p className="text-sm text-gray-400">Run a site audit to check for SEO issues</p>
    </div>
  );

  const issues: any[] = (() => { try { return JSON.parse(latest.issues || "[]"); } catch { return []; } })();
  const perf: any = (() => { try { return JSON.parse(latest.performance || "{}"); } catch { return {}; } })();
  const criticals = issues.filter((i) => i.sev === "critical");
  const warnings = issues.filter((i) => i.sev === "warning");
  const infos = issues.filter((i) => i.sev === "info");

  // Radar chart for scores
  const radarData = [
    { subject: "SEO", score: latest.seoScore || 0 },
    { subject: "Performance", score: latest.performanceScore || 0 },
    { subject: "Accessibility", score: latest.accessibilityScore || 0 },
    { subject: "Best Practices", score: latest.bestPracticesScore || 0 },
    { subject: "Mobile", score: latest.mobileScore || 0 },
    { subject: "Desktop", score: latest.desktopScore || 0 },
  ];

  // Audit history chart
  const historyData = [...audits].reverse().map((a: any) => ({
    date: new Date(a.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    score: a.score, seo: a.seoScore, perf: a.performanceScore,
  }));

  return (
    <div className="space-y-6">
      {/* Overall Score + Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        {/* Big score */}
        <div className="md:col-span-2 card p-6 flex flex-col items-center justify-center">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold border-4 ${latest.score >= 80 ? "border-green-500 text-green-600 bg-green-50" : latest.score >= 60 ? "border-yellow-500 text-yellow-600 bg-yellow-50" : "border-red-500 text-red-600 bg-red-50"}`}>
            {latest.score}
          </div>
          <p className="text-sm font-medium mt-3">Overall Health Score</p>
          <p className="text-xs text-gray-400 mt-1">Grade: {scoreGrade(latest.score)}</p>
          <div className="flex gap-4 mt-3 text-xs text-gray-500">
            <span>{latest.pagesAudited} pages</span>
            <span>{latest.passed} passed</span>
          </div>
        </div>
        {/* Individual score cards */}
        {[
          { label: "SEO Score", value: latest.seoScore, icon: Search },
          { label: "Performance", value: latest.performanceScore, icon: Zap },
          { label: "Accessibility", value: latest.accessibilityScore, icon: Eye },
          { label: "Best Practices", value: latest.bestPracticesScore, icon: CheckCircle2 },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="card p-4 flex flex-col items-center justify-center">
              <Icon className={`w-5 h-5 mb-2 ${scoreColor(s.value)}`} />
              <p className={`text-2xl font-bold ${scoreColor(s.value)}`}>{s.value ?? "—"}</p>
              <p className="text-[10px] text-gray-500 mt-1">{s.label}</p>
              <p className="text-[10px] text-gray-400">{scoreGrade(s.value)}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold mb-4">Score Breakdown</h3>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
              <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Radar dataKey="score" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Audit History */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold mb-4">Audit Score History</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={historyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="score" name="Overall" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="seo" name="SEO" stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
              <Line type="monotone" dataKey="perf" name="Performance" stroke="#10b981" strokeWidth={1.5} strokeDasharray="5 5" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Core Web Vitals */}
      {perf && Object.keys(perf).length > 0 && (
        <div className="card p-5">
          <h3 className="text-sm font-semibold mb-4 flex items-center gap-2"><Gauge className="w-4 h-4 text-violet-600" /> Core Web Vitals</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: "lcp", label: "LCP", unit: "s", good: 2.5, poor: 4.0, desc: "Largest Contentful Paint" },
              { key: "fid", label: "FID", unit: "ms", good: 100, poor: 300, desc: "First Input Delay" },
              { key: "cls", label: "CLS", unit: "", good: 0.1, poor: 0.25, desc: "Cumulative Layout Shift" },
              { key: "ttfb", label: "TTFB", unit: "ms", good: 200, poor: 600, desc: "Time to First Byte" },
              { key: "fcp", label: "FCP", unit: "s", good: 1.8, poor: 3.0, desc: "First Contentful Paint" },
              { key: "si", label: "SI", unit: "s", good: 3.4, poor: 5.8, desc: "Speed Index" },
              { key: "tti", label: "TTI", unit: "s", good: 3.8, poor: 7.3, desc: "Time to Interactive" },
              { key: "tbt", label: "TBT", unit: "ms", good: 200, poor: 600, desc: "Total Blocking Time" },
            ].map((v) => {
              const val = perf[v.key];
              if (val == null) return null;
              const status = val <= v.good ? "good" : val <= v.poor ? "needs-improvement" : "poor";
              const colors = { good: "text-green-600 bg-green-50", "needs-improvement": "text-yellow-600 bg-yellow-50", poor: "text-red-600 bg-red-50" };
              return (
                <div key={v.key} className={`rounded-xl p-3 ${colors[status]}`}>
                  <p className="text-xs font-medium opacity-70">{v.desc}</p>
                  <p className="text-2xl font-bold mt-1">{typeof val === "number" ? val.toFixed(v.key === "cls" ? 2 : 1) : val}{v.unit}</p>
                  <p className="text-[10px] mt-1 capitalize">{status.replace("-", " ")}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Issues */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Issues ({issues.length})</h3>
          <div className="flex gap-2 text-xs">
            {criticals.length > 0 && <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700">{criticals.length} Critical</span>}
            {warnings.length > 0 && <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">{warnings.length} Warnings</span>}
            {infos.length > 0 && <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{infos.length} Info</span>}
          </div>
        </div>
        <div className="divide-y divide-gray-50">
          {[...criticals, ...warnings, ...infos].map((issue, i) => {
            const Icon = SEV_ICONS[issue.sev] || Info;
            return (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${SEV_COLORS[issue.sev]}`}><Icon className="w-4 h-4" /></div>
                <div className="flex-1">
                  <p className="text-sm">{issue.msg}</p>
                  <p className="text-[10px] text-gray-400 capitalize">{issue.cat} · {issue.sev}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Device scores */}
      <div className="grid grid-cols-2 gap-4">
        <div className="card p-5 flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${latest.mobileScore >= 80 ? "bg-green-50" : latest.mobileScore >= 60 ? "bg-yellow-50" : "bg-red-50"}`}>
            <Smartphone className={`w-6 h-6 ${scoreColor(latest.mobileScore)}`} />
          </div>
          <div>
            <p className={`text-2xl font-bold ${scoreColor(latest.mobileScore)}`}>{latest.mobileScore ?? "—"}</p>
            <p className="text-xs text-gray-500">Mobile Score</p>
          </div>
        </div>
        <div className="card p-5 flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${latest.desktopScore >= 80 ? "bg-green-50" : latest.desktopScore >= 60 ? "bg-yellow-50" : "bg-red-50"}`}>
            <Monitor className={`w-6 h-6 ${scoreColor(latest.desktopScore)}`} />
          </div>
          <div>
            <p className={`text-2xl font-bold ${scoreColor(latest.desktopScore)}`}>{latest.desktopScore ?? "—"}</p>
            <p className="text-xs text-gray-500">Desktop Score</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   BACKLINKS TAB
   ════════════════════════════════════════════════════════════ */
function BacklinksTab({ data }: any) {
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterFollow, setFilterFollow] = useState<string>("all");

  if (!data) return null;
  const { backlinks = [], summary } = data;

  const filtered = useMemo(() => {
    let bls = [...backlinks];
    if (filterStatus !== "all") bls = bls.filter((b: any) => b.status === filterStatus);
    if (filterFollow === "dofollow") bls = bls.filter((b: any) => b.doFollow);
    if (filterFollow === "nofollow") bls = bls.filter((b: any) => !b.doFollow);
    return bls;
  }, [backlinks, filterStatus, filterFollow]);

  // Authority distribution for chart
  const authDist = [
    { range: "90-100", count: backlinks.filter((b: any) => (b.authority || 0) >= 90).length, color: "#10b981" },
    { range: "70-89", count: backlinks.filter((b: any) => (b.authority || 0) >= 70 && (b.authority || 0) < 90).length, color: "#3b82f6" },
    { range: "50-69", count: backlinks.filter((b: any) => (b.authority || 0) >= 50 && (b.authority || 0) < 70).length, color: "#f59e0b" },
    { range: "30-49", count: backlinks.filter((b: any) => (b.authority || 0) >= 30 && (b.authority || 0) < 50).length, color: "#f97316" },
    { range: "0-29", count: backlinks.filter((b: any) => (b.authority || 0) < 30).length, color: "#ef4444" },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {[
            { l: "Total Backlinks", v: summary.total, icon: Link2 },
            { l: "Do-Follow", v: summary.doFollow, icon: CheckCircle2 },
            { l: "No-Follow", v: summary.noFollow, icon: XCircle },
            { l: "Active", v: summary.active, icon: Zap },
            { l: "Lost", v: summary.lost, icon: AlertTriangle },
            { l: "Avg Authority", v: summary.avgAuthority, icon: Award },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.l} className="card p-3 text-center">
                <Icon className="w-4 h-4 mx-auto mb-1 text-gray-400" />
                <p className="text-xl font-bold text-gray-900">{s.v}</p>
                <p className="text-[10px] text-gray-500">{s.l}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* DoFollow vs NoFollow */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold mb-4">Follow Distribution</h3>
          {summary && (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={[{ name: "Do-Follow", value: summary.doFollow }, { name: "No-Follow", value: summary.noFollow }]} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3}>
                  <Cell fill="#10b981" /><Cell fill="#ef4444" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex justify-center gap-4 text-xs mt-2">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" />Do-Follow</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" />No-Follow</span>
          </div>
        </div>

        {/* Authority Distribution */}
        <div className="lg:col-span-2 card p-5">
          <h3 className="text-sm font-semibold mb-4">Authority Distribution</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={authDist}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="range" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" name="Backlinks" radius={[6, 6, 0, 0]}>
                {authDist.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select className="input w-auto" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option><option value="active">Active</option><option value="lost">Lost</option><option value="broken">Broken</option>
        </select>
        <select className="input w-auto" value={filterFollow} onChange={(e) => setFilterFollow(e.target.value)}>
          <option value="all">All Types</option><option value="dofollow">Do-Follow</option><option value="nofollow">No-Follow</option>
        </select>
      </div>

      {/* Backlinks Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Source</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Target</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Anchor</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">DA</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Type</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Status</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">First Seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.slice(0, 50).map((bl: any) => (
                <tr key={bl.id} className="hover:bg-gray-50/50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-violet-600">{bl.sourceDomain}</p>
                    <p className="text-[10px] text-gray-400 truncate max-w-[200px]">{bl.sourceUrl}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[150px]">{bl.targetUrl?.replace(/^https?:\/\//, "")}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{bl.anchorText || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-bold ${bl.authority >= 70 ? "text-green-600" : bl.authority >= 40 ? "text-blue-600" : "text-gray-600"}`}>{bl.authority ?? "—"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${bl.doFollow ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`}>{bl.doFollow ? "Follow" : "NoFollow"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${bl.status === "active" ? "bg-emerald-50 text-emerald-700" : bl.status === "lost" ? "bg-red-50 text-red-700" : "bg-orange-50 text-orange-700"}`}>{bl.status}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{new Date(bl.firstSeen).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > 50 && <div className="px-4 py-2 text-xs text-gray-400 bg-gray-50 border-t">Showing 50 of {filtered.length} backlinks</div>}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   COMPETITORS TAB
   ════════════════════════════════════════════════════════════ */
function CompetitorsTab({ data, project }: any) {
  if (!data || !project) return null;
  const competitors = data || [];

  if (!competitors.length) return (
    <div className="card p-12 text-center">
      <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h3 className="font-semibold text-gray-700 mb-1">No Competitors Tracked</h3>
      <p className="text-sm text-gray-400">Add competitors to compare keyword overlap and traffic</p>
    </div>
  );

  // Comparison chart data
  const chartData = competitors.map((c: any) => ({
    name: c.name || c.domain,
    "Domain Authority": c.domainAuthority || 0,
    "Organic Keywords": Math.round((c.organicKeywords || 0) / 1000),
    "Common Keywords": c.commonKeywords || 0,
  }));

  // Traffic chart
  const trafficData = competitors.map((c: any) => ({ name: c.name || c.domain, traffic: c.organicTraffic }));

  return (
    <div className="space-y-6">
      {/* Competitor Comparison Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Your project card */}
        <div className="card p-4 border-2 border-violet-200 bg-violet-50/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-violet-600 to-pink-500 flex items-center justify-center text-white text-sm font-bold">{project.name?.[0] || "Y"}</div>
            <div>
              <p className="font-semibold text-sm text-violet-700">{project.name} (You)</p>
              <p className="text-xs text-gray-400">{project.domain}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div><p className="text-lg font-bold">{project.healthScore || "—"}</p><p className="text-[10px] text-gray-500">Health</p></div>
            <div><p className="text-lg font-bold">{project._count?.keywordRanks || 0}</p><p className="text-[10px] text-gray-500">Keywords</p></div>
            <div><p className="text-lg font-bold">{project._count?.seoBacklinks || 0}</p><p className="text-[10px] text-gray-500">Backlinks</p></div>
          </div>
        </div>

        {/* Competitor cards */}
        {competitors.map((c: any) => (
          <div key={c.id} className="card p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600 text-sm font-bold">{(c.name || c.domain)[0]}</div>
              <div>
                <p className="font-semibold text-sm">{c.name || c.domain}</p>
                <p className="text-xs text-gray-400">{c.domain}</p>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1 text-center">
              <div><p className="text-lg font-bold text-blue-600">{c.domainAuthority || "—"}</p><p className="text-[10px] text-gray-500">DA</p></div>
              <div><p className="text-lg font-bold">{fmt(c.organicKeywords)}</p><p className="text-[10px] text-gray-500">Keywords</p></div>
              <div><p className="text-lg font-bold">{fmt(c.organicTraffic)}</p><p className="text-[10px] text-gray-500">Traffic</p></div>
              <div><p className="text-lg font-bold">{fmt(c.backlinks)}</p><p className="text-[10px] text-gray-500">Backlinks</p></div>
            </div>
            {c.commonKeywords > 0 && <p className="text-[10px] text-violet-600 mt-2">{c.commonKeywords} common keywords</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* DA Comparison */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold mb-4">Domain Authority Comparison</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize: 11 }} domain={[0, 100]} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={100} />
              <Tooltip />
              <Bar dataKey="Domain Authority" fill="#8b5cf6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Traffic Comparison */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold mb-4">Organic Traffic Comparison</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={trafficData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmt(v)} />
              <Tooltip formatter={(v: number) => fmt(v)} />
              <Bar dataKey="traffic" name="Monthly Traffic" fill="#ec4899" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b bg-gray-50"><h3 className="text-sm font-semibold">Competitor Analysis</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Competitor</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">DA</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Organic Keywords</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Monthly Traffic</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Backlinks</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Common Keywords</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Top Keywords</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {competitors.map((c: any) => {
                const topKws: string[] = (() => { try { return JSON.parse(c.topKeywords || "[]"); } catch { return []; } })();
                return (
                  <tr key={c.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3"><p className="text-sm font-medium">{c.name || c.domain}</p><p className="text-[10px] text-gray-400">{c.domain}</p></td>
                    <td className="px-4 py-3"><span className={`text-sm font-bold ${c.domainAuthority >= 80 ? "text-green-600" : c.domainAuthority >= 60 ? "text-blue-600" : "text-gray-600"}`}>{c.domainAuthority}</span></td>
                    <td className="px-4 py-3 text-sm">{c.organicKeywords?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm">{c.organicTraffic?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm">{c.backlinks?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm font-medium text-violet-600">{c.commonKeywords}</td>
                    <td className="px-4 py-3"><div className="flex gap-1 flex-wrap">{topKws.slice(0, 3).map((k, i) => <span key={i} className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{k}</span>)}</div></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   PAGES TAB
   ════════════════════════════════════════════════════════════ */
function PagesTab({ data }: any) {
  const [sortBy, setSortBy] = useState("contentScore");

  if (!data) return null;
  const { pages = [], summary } = data;

  const sorted = useMemo(() => [...pages].sort((a: any, b: any) => (b[sortBy] || 0) - (a[sortBy] || 0)), [pages, sortBy]);

  return (
    <div className="space-y-6">
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: "Total Pages", v: summary.totalPages, icon: FileText },
            { l: "Avg Content Score", v: summary.avgScore, icon: Award },
            { l: "Pages with Issues", v: summary.issuePages, icon: AlertTriangle },
            { l: "Avg Load Time", v: `${summary.avgLoadTime}s`, icon: Zap },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.l} className="card p-3 text-center">
                <Icon className="w-4 h-4 mx-auto mb-1 text-gray-400" />
                <p className="text-xl font-bold text-gray-900">{s.v}</p>
                <p className="text-[10px] text-gray-500">{s.l}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Sort */}
      <div className="flex gap-3">
        <select className="input w-auto" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="contentScore">Sort by Content Score</option>
          <option value="loadTime">Sort by Load Time</option>
          <option value="wordCount">Sort by Word Count</option>
          <option value="mobileScore">Sort by Mobile Score</option>
        </select>
      </div>

      {/* Page Cards */}
      <div className="space-y-3">
        {sorted.map((page: any) => {
          const issues: any[] = (() => { try { return JSON.parse(page.issuesFound || "[]"); } catch { return []; } })();
          return (
            <div key={page.id} className="card p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${page.statusCode === 200 ? "bg-green-50 text-green-700" : page.statusCode === 301 ? "bg-yellow-50 text-yellow-700" : "bg-red-50 text-red-700"}`}>{page.statusCode}</span>
                    <p className="text-sm font-medium text-violet-600 truncate">{page.url?.replace(/^https?:\/\//, "")}</p>
                  </div>
                  {page.title && <p className="text-xs text-gray-600 mt-1 truncate">{page.title} <span className="text-gray-400">({page.titleLength} chars)</span></p>}
                  {page.metaDescription && <p className="text-[10px] text-gray-400 mt-0.5 truncate">{page.metaDescription}</p>}
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ml-3 text-sm font-bold ${(page.contentScore || 0) >= 80 ? "bg-green-50 text-green-600" : (page.contentScore || 0) >= 60 ? "bg-yellow-50 text-yellow-600" : "bg-red-50 text-red-600"}`}>
                  {page.contentScore ?? "—"}
                </div>
              </div>

              <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><FileText className="w-3 h-3" />{page.wordCount} words</span>
                <span className="flex items-center gap-1"><Zap className="w-3 h-3" />{page.loadTime?.toFixed(1)}s</span>
                <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" />Mobile: {page.mobileScore ?? "—"}</span>
                <span className="flex items-center gap-1"><Link2 className="w-3 h-3" />{page.internalLinks} int / {page.externalLinks} ext</span>
                <span>H1: {page.h1Count}</span>
                <span>Images: {page.images}{page.imagesNoAlt > 0 && <span className="text-red-500"> ({page.imagesNoAlt} no alt)</span>}</span>
                {page.hasCanonical && <span className="text-green-600">✓ Canonical</span>}
                {!page.isIndexable && <span className="text-red-600">✗ No-Index</span>}
              </div>

              {issues.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {issues.map((iss: any, i: number) => (
                    <span key={i} className={`text-[10px] px-2 py-0.5 rounded border ${SEV_COLORS[iss.sev] || "text-gray-600 bg-gray-50"}`}>{iss.msg}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   AI SEO TAB
   ════════════════════════════════════════════════════════════ */
function AITab({ projects }: any) {
  const [keyword, setKeyword] = useState("");
  const [domain, setDomain] = useState(projects?.[0]?.domain || "");
  const [analysisType, setAnalysisType] = useState("keywords");
  const [result, setResult] = useState<any>(null);

  const seoMut = useMutation({
    mutationFn: (body: any) => api.post<any>("/ai/generate/seo", body),
    onSuccess: (data) => setResult(data),
  });

  const analysisTypes = [
    { id: "keywords", label: "Keyword Research", desc: "Find related keywords, search volume estimates & content ideas", icon: Search },
    { id: "content", label: "Content Optimizer", desc: "Get recommendations to improve content for target keyword", icon: FileText },
    { id: "technical", label: "Technical SEO", desc: "Review technical SEO factors and get improvement tips", icon: Database },
    { id: "competitor", label: "Competitor Analysis", desc: "Analyze what competitors rank for and find gaps", icon: Target },
  ];

  return (
    <div className="space-y-6">
      {/* Analysis Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {analysisTypes.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setAnalysisType(t.id)} className={`card p-4 text-left transition-all ${analysisType === t.id ? "border-violet-300 bg-violet-50/50 shadow-md" : "hover:border-gray-300"}`}>
              <Icon className={`w-5 h-5 mb-2 ${analysisType === t.id ? "text-violet-600" : "text-gray-400"}`} />
              <p className="text-sm font-semibold">{t.label}</p>
              <p className="text-[10px] text-gray-400 mt-1">{t.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Input */}
      <div className="card p-5">
        <div className="bg-gradient-to-r from-violet-600 via-purple-500 to-pink-500 -mx-5 -mt-5 px-5 py-4 rounded-t-xl mb-5">
          <h3 className="text-white font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5" /> AI SEO Advisor</h3>
          <p className="text-white/70 text-xs mt-1">Powered by AI to give you actionable SEO insights</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Target Keyword / Topic *</label>
            <input className="input" placeholder="e.g. cloud hosting for small business" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
          </div>
          <div>
            <label className="label">Your Domain</label>
            <input className="input" placeholder="example.com" value={domain} onChange={(e) => setDomain(e.target.value)} />
          </div>
        </div>
        <button onClick={() => seoMut.mutate({ keyword, domain, type: analysisType })} disabled={!keyword || seoMut.isPending} className="btn-primary mt-4">
          {seoMut.isPending ? "Analyzing..." : "🔍 Analyze"}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="card p-5 space-y-5">
          <h3 className="text-sm font-semibold flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> Analysis Results</h3>

          {result.titleSuggestions && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Title Tag Suggestions</h4>
              <div className="space-y-1">
                {result.titleSuggestions.map((t: string, i: number) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                    <p className="text-sm">{t}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${t.length <= 60 ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{t.length} chars</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.metaDescriptions && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Meta Description Suggestions</h4>
              <div className="space-y-1">
                {result.metaDescriptions.map((m: string, i: number) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm">{m}</p>
                    <span className={`text-[10px] ${m.length <= 160 ? "text-green-600" : "text-red-600"}`}>{m.length} chars</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.relatedKeywords && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Related Keywords</h4>
              <div className="flex flex-wrap gap-2">
                {result.relatedKeywords.map((k: string, i: number) => (
                  <span key={i} className="text-sm px-3 py-1.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">{k}</span>
                ))}
              </div>
            </div>
          )}

          {result.contentOutline && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Content Outline</h4>
              <div className="space-y-1">
                {result.contentOutline.map((item: string, i: number) => (
                  <p key={i} className="text-sm text-gray-700 bg-gray-50 p-2 rounded-lg">{item}</p>
                ))}
              </div>
            </div>
          )}

          {result.tips && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Actionable Tips</h4>
              <div className="space-y-2">
                {result.tips.map((t: string, i: number) => (
                  <div key={i} className="flex items-start gap-2 bg-emerald-50 rounded-lg p-3">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-700">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
